import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { BusyState, ImportIssue, ImportResult, ImportSchedule } from "../models/schedule.model";
import { downloadText, fmtDateTime, fmtWindow, toCsv } from "../schedule.utils";

/**
 * Presentational screen: shows the result of import_schedule and emits
 * the user's decision. It never calls the API itself.
 */
@Component({
  selector: "app-review-upload",
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ["./review-upload.component.css"],
  templateUrl: "./review-upload.component.html",
})
export class ReviewUploadComponent {
  @Input() result!: ImportResult;
  @Input() uploadedBy = "";
  @Input() busy: BusyState = "idle";

  @Output() uploadCorrected = new EventEmitter<void>();
  @Output() discard = new EventEmitter<void>();
  @Output() publish = new EventEmitter<void>();

  get schedules(): ImportSchedule[] {
    return this.result?.schedules || [];
  }

  get issues(): ImportIssue[] {
    return this.result?.issues || [];
  }

  get errorCount(): number {
    return this.issues.filter((i) => i.severity == "error").length;
  }

  get warningCount(): number {
    return this.issues.filter((i) => i.severity == "warning").length;
  }

  /** Rows with errors are skipped by the backend, so publishing only needs something valid. */
  get canPublish(): boolean {
    return this.schedules.length > 0 && this.busy == "idle";
  }

  get badge(): { text: string; cls: string } {
    if (this.schedules.length == 0) {
      return { text: "Nothing to publish", cls: "pill-err" };
    }
    if (this.errorCount > 0) {
      return { text: "Rows with errors will be skipped", cls: "pill-warn" };
    }
    return { text: "Ready to publish", cls: "pill-ok" };
  }

  get uploadedAt(): string {
    return fmtDateTime(this.result?.imported_at);
  }

  get publishLabel(): string {
    if (this.busy == "publishing") {
      return "Publishing\u2026";
    }
    const n = this.schedules.length;
    return `Publish ${n} schedule${n == 1 ? "" : "s"}`;
  }

  windowText(s: ImportSchedule): string {
    return fmtWindow(s.window_start, s.window_end);
  }

  rebootText(s: ImportSchedule): string {
    return s.reboot_option == "RebootIfNeeded" ? "Reboot if needed" : s.reboot_option;
  }

  owners(s: ImportSchedule): string {
    return (s.stakeholders || []).map((x) => x.owner).join(", ");
  }

  missed(s: ImportSchedule): number {
    return (s.notices || []).filter((n) => n.status == "Missed").length;
  }

  downloadIssues(): void {
    const csv = toCsv(
      ["severity", "row", "instance_id", "name", "message"],
      this.issues.map((i) => [i.severity, i.row, i.instance_id, i.name, i.message])
    );
    downloadText(`${this.result.import_id}_issues.csv`, csv);
  }

  trackSchedule(_: number, s: ImportSchedule) {
    return s.schedule_id;
  }

  trackIssue(i: number, issue: ImportIssue) {
    return `${issue.row}-${issue.instance_id}-${i}`;
  }
}